/**
 * listPerformance: which prospect lists performed best, for the client portal.
 *
 * A "list" is the prospect's `source`, the grouping already used by the
 * dialer's list filter and by ListCompletionTable (analytics). A call belongs
 * to a list when its number matches a prospect of that list, compared on
 * digits only: calls carry no list id. A number present in two lists counts
 * in both, as in ListCompletionTable.
 *
 * Calls whose number matches no prospect (an unknown inbound caller, a
 * prospect since deleted) belong to no list: they appear in "all lists" and
 * in no row.
 */
import { hasMeetingBooked } from '@/utils/callUtils';

export interface ListCall {
  prospect_phone?: string | null;
  to_number?: string | null;
  call_outcome?: string | null;
  meeting_booked?: boolean | null;
}

export interface ListProspect {
  prospect_phone?: string | null;
}

export interface ListPerformanceRow {
  list: string;
  /** Prospects in the list. */
  contacts: number;
  /** Prospects called at least once. */
  called: number;
  /** Calls placed to the list. */
  calls: number;
  /** Prospects with at least one call ending in a connected outcome. */
  reached: number;
  /** Calls that booked a meeting. */
  meetings: number;
  /** reached / called, in percent. */
  reachRate: number;
  /** meetings / called, in percent. */
  meetingRate: number;
}

/** Outcomes that count as "contact established", same set as the portal cards. */
export const CONNECTED_OUTCOMES = new Set([
  'meeting_booked',
  'contacted',
  'not_interested',
  'callback_requested',
]);

export const digits = (phone: unknown): string => String(phone || '').replace(/\D/g, '');

function callsByPhone<C extends ListCall>(calls: C[]): Map<string, C[]> {
  const map = new Map<string, C[]>();
  for (const c of calls) {
    const k = digits(c.prospect_phone || c.to_number);
    if (!k) continue;
    const bucket = map.get(k);
    if (bucket) bucket.push(c);
    else map.set(k, [c]);
  }
  return map;
}

/** The calls placed to one list, each call once even if the number repeats. */
export function callsOfList<C extends ListCall>(prospects: ListProspect[], calls: C[]): C[] {
  const phones = new Set(prospects.map((p) => digits(p.prospect_phone)).filter(Boolean));
  return calls.filter((c) => phones.has(digits(c.prospect_phone || c.to_number)));
}

/**
 * One row per list, best first: most meetings, then highest reach rate, then
 * most calls. A list never called sorts last.
 */
export function listPerformance(
  prospectsBySource: Record<string, ListProspect[]>,
  calls: ListCall[],
): ListPerformanceRow[] {
  const byPhone = callsByPhone(calls || []);

  return Object.entries(prospectsBySource || {})
    .map(([list, prospects]) => {
      let called = 0;
      let callCount = 0;
      let reached = 0;
      let meetings = 0;
      const seen = new Set<string>();

      for (const p of prospects) {
        const k = digits(p.prospect_phone);
        // The same number twice in one list is one contact, not two.
        if (!k || seen.has(k)) continue;
        seen.add(k);
        const own = byPhone.get(k);
        if (!own || own.length === 0) continue;
        called += 1;
        callCount += own.length;
        if (own.some((c) => c.call_outcome != null && CONNECTED_OUTCOMES.has(c.call_outcome))) reached += 1;
        meetings += own.filter(hasMeetingBooked).length;
      }

      return {
        list,
        contacts: prospects.length,
        called,
        calls: callCount,
        reached,
        meetings,
        reachRate: called > 0 ? (reached / called) * 100 : 0,
        meetingRate: called > 0 ? (meetings / called) * 100 : 0,
      };
    })
    .sort((a, b) =>
      b.meetings - a.meetings
      || b.reachRate - a.reachRate
      || b.calls - a.calls
      || a.list.localeCompare(b.list));
}
